import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MIN_COLUMN_WIDTH = 5;
const MAX_COLUMN_WIDTH = 100;
const PADDING = 2;
const ASCII_A = 65; // ASCII code for 'A'
const FOOTER_ROW_OFFSET = 3;
const UNGROUPED = 'Ungrouped';
const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';


/**
 * Sanitizes worksheet name to comply with Excel naming rules
 * Max 31 characters, Cannot contain: \ / ? * [ ] :, Cannot be empty
 * @param {string} name - Original name
 * @returns {string} - Sanitized name
 */
const sanitizeWorksheetName = (name, index) => {
  if (!name) {
    return `Sheet_${index + 1}`;
  }
  const sanitizedName = name
    .replace(/[\\/?*[\]:]/g, '')
    .substring(0, 31)
    .trim();

  // Check if sanitized name contains only underscores or is empty
  if (!sanitizedName) {
    return `Sheet_${index + 1}`;
  }

  return sanitizedName;
};





// Helper function to check if value should be marked as 'Ungrouped'
const shouldMarkAsUngrouped = (value) => {
  // Check for null or undefined
  if (value === null || value === undefined) {
    return true;
  }

  // Check for complex types (objects and arrays)
  if ((typeof value === 'object' && value !== null) || Array.isArray(value)) {
    return true;
  }

  // For strings, check multiple conditions
  if (typeof value === 'string') {
    // Empty string or only whitespace
    if (value.trim() === '') {
      return true;
    }

    // Contains only special characters
    const onlySpecialCharsRegex = /^[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~\s]+$/;
    const hasSpecialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

    if (onlySpecialCharsRegex.test(value) && hasSpecialCharRegex.test(value)) {
      return true;
    }
  }
  return false;
};




/**
 * Creates a totals row using reduce function
 * @param {Array<Object>} data - Array of objects to calculate totals for
 * @param {Array<string>} showTotalsFor - Columns to calculate totals for
 * @returns {Object} - Totals row object
 */
const addTotalsRow = (columns, data, showTotalsFor) => {
  if (!data || data.length === 0 || !showTotalsFor || !Array.isArray(showTotalsFor)) {
    console.error('Data and showTotalsFor property is required to show totals');
    return {};
  }

  const totalsRow = data.reduce((acc, item, index) => {
    if (index === 0) {
      columns.forEach((column, colIndex) => {
        if (colIndex === 0) {
          acc[column.key] = 'Total';
        } else if (showTotalsFor.includes(column.key)) {
          acc[column.key] = 0;
        } else {
          acc[column.key] = '';
        }
      });
    }

    columns.forEach((column, colIndex) => {
      if (colIndex !== 0 && showTotalsFor.includes(column.key)) {
        const value = item[column.key];

        if (typeof value === 'number' && !isNaN(value)) {
          acc[column.key] += value;
        }
        else if (typeof value === 'string' && value.trim() !== '') {
          const numValue = parseInt(value);
          if (!isNaN(numValue)) {
            acc[column.key] += numValue;
          }
        }
      }
    });
    return acc;
  }, {});
  return totalsRow;
};





/** 
 * @description
 * This function creates an Excel workbook from JSON data with the following features:
 * - Auto-generates column headers from data keys or uses custom column definitions
 * - Groups data into separate worksheets based on a specified field
 * - Calculates and displays totals for numeric columns
 * - Applies consistent formatting (bold headers, left alignment, borders for totals)
 * - Adds customizable footer text
 * - Sanitizes worksheet names to comply with Excel naming rules
 * - Handles edge cases like null/undefined grouping values
 * 
 * @param {Object} config - Configuration object for the Excel export
 * @param {string} config.fileName - Name of the Excel file (without .xlsx extension)
 * 
 * @param {string} [config.worksheetName] - Name of the worksheet when not grouping data.
 *                                          If not provided, uses fileName as Sheet_1.
 *                                          Max 31 characters, auto-sanitized to remove invalid chars: \ / ? * [ ] :
 * 
 * @param {Array<Object>} [config.worksheetColumns] - Optional column definitions array.
 *                                                    If not provided, columns will be auto-generated from data keys.
 * @param {string} config.worksheetColumns[].header - Display name for the column header
 * @param {string} config.worksheetColumns[].key - Property key from data objects to map to this column
 * 
 * @param {Array<Object>} [config.JSONdata] - Array of data objects to export. Each object represents a row.
 *                                           If empty/null/undefined, creates empty workbook.
 * 
 * @param {string} [config.footerText] - Optional footer text to display at the bottom of each worksheet.
 *                                      Text will be centered, bold, and placed 3 rows below the last data row.
 * 
 * @param {string} [config.groupBy] - Property name to group data by. Creates separate worksheets for each unique value.
 *                                   Values that are null, undefined, empty strings, or contain only special characters
 *                                   will be grouped under "Ungrouped" worksheet.
 * 
 * @param {boolean} [config.showTotals=false] - Whether to add a totals row at the bottom of each worksheet.
 *                                             Totals row is formatted with bold font and borders.
 *                                             Not applied to "Ungrouped" worksheets when grouping is enabled.
 * 
 * @param {Array<string>} [config.showTotalsFor] - Array of column keys to calculate totals for.
 *                                                Must be provided if showTotals is true.
 *                                                Only numeric values (numbers or parseable strings) are summed.
 *                                                First column gets "Total" label, specified columns get sums, others are empty.
 * 
 */
export const exportToExcel = async ({ fileName, worksheetName, worksheetColumns, JSONdata, footerText, groupBy, showTotals, showTotalsFor }) => {
  const workbook = new ExcelJS.Workbook();

  const createWorkSheet = (worksheetName, worksheetData, worksheetColumns, groupKey) => {
    const worksheet = workbook.addWorksheet(worksheetName);

    // Dynamically set columns if not provided.
    if (worksheetData && worksheetData.length) {

      // Set the rows and columns for the worksheet
      worksheet.columns = worksheetColumns;
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      worksheet.addRows(worksheetData);

      // Auto-adjust column widths based on content
      worksheet.columns.forEach((column, index) => {
        const columnLetter = String.fromCharCode(ASCII_A + index); // A, B, C, etc.
        let maxWidth = MIN_COLUMN_WIDTH; // minimum width

        for (let row = 1; row <= worksheet.rowCount; row++) {
          const cell = worksheet.getCell(`${columnLetter}${row}`);
          if (cell.value) {
            const cellLength = String(cell.value).length;
            maxWidth = Math.max(maxWidth, cellLength);
          }
        }
        column.width = Math.min(maxWidth + PADDING, MAX_COLUMN_WIDTH);
      });

      // Align all cells to left
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.alignment = { horizontal: 'left' };
        });
      });

      // Format the last row (totals row) if it exists
      if (showTotals && showTotalsFor && showTotalsFor.length && groupKey !== UNGROUPED) {
        const lastRow = worksheet.lastRow;
        lastRow.font = { bold: true };
        lastRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber <= worksheetColumns.length) {
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' }
            };
          }
        });

      }

      // Add footer text if provided
      if (footerText) {
        const footerRowNumber = worksheet.lastRow.number + FOOTER_ROW_OFFSET;
        worksheet.getCell(`A${footerRowNumber}`).value = footerText;
        const lastColumnLetter = worksheet.columns[worksheet.columns.length - 1].letter;
        worksheet.mergeCells(`A${footerRowNumber}:${lastColumnLetter}${footerRowNumber}`);
        worksheet.getCell(`A${footerRowNumber}`).alignment = { horizontal: 'center' };
        worksheet.getCell(`A${footerRowNumber}`).font = { bold: true, size: 12 };
      }
    }
  }

  // Check if we need to group data
  if (JSONdata && JSONdata.length > 0) {

    if (!worksheetColumns) {
      worksheetColumns = Object.keys(JSONdata[0]).map(key => ({
        header: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
        key,
      }));
    }
    if (groupBy) {
      const groupedData = JSONdata.reduce((groups, item) => {
        let groupKey = item[groupBy];
        if (shouldMarkAsUngrouped(groupKey)) {
          groupKey = UNGROUPED;
        } else {
          groupKey = String(groupKey).trim();
        }

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
        return groups;
      }, {});

      // Create a worksheet for each group
      Object.keys(groupedData).forEach((groupKey, index) => {
        const sanitizedWorksheetName = sanitizeWorksheetName(groupKey, index);
        let finalGroupedData = [...groupedData[groupKey]];
        if (showTotals && groupKey !== UNGROUPED) {
          const totalsRow = addTotalsRow(worksheetColumns, finalGroupedData, showTotalsFor);
          finalGroupedData = [...groupedData[groupKey], totalsRow];
        }
        createWorkSheet(sanitizedWorksheetName, finalGroupedData, worksheetColumns, groupKey);
      });

    } else {
      // Single worksheet (no grouping)
      const sanitizedWorksheetName = sanitizeWorksheetName(worksheetName, 0);
      if (showTotals) {
        const totalsRow = addTotalsRow(worksheetColumns, JSONdata, showTotalsFor);
        JSONdata = [...JSONdata, totalsRow];
      }
      createWorkSheet(sanitizedWorksheetName, JSONdata, worksheetColumns);
    }
  }

  // Generate the Excel file and trigger the download
  const buffer = await workbook.xlsx.writeBuffer();
  const data = new Blob([buffer], {
    type: EXCEL_MIME_TYPE,
  });
  saveAs(data, `${fileName}.xlsx`);
};